/**
 * Client receipt paper format (thermal 58/80 mm roll vs A5).
 * Roll "80 mm" → Chromium pageWidth uses media ~72 mm (printable band / driver form).
 */

import { kvGet, kvSet } from '../infrastructure/storage/kvAccess';
import {
  DEFAULT_THERMAL_PAPER,
  THERMAL_RECEIPT_PRINT_MARGINS,
  resolveThermalPaperProfile,
  resolveThermalPaperWidthKey,
  thermalMediaWidthMm
} from '../services/thermalReceipt/thermalReceiptConfig';

export const CLIENT_RECEIPT_PAPER_THERMAL_80 = 'thermal80';
export const CLIENT_RECEIPT_PAPER_THERMAL_58 = 'thermal58';
export const CLIENT_RECEIPT_PAPER_A5 = 'A5';

export const CLIENT_RECEIPT_PAPER_FORMATS = [
  CLIENT_RECEIPT_PAPER_THERMAL_80,
  CLIENT_RECEIPT_PAPER_THERMAL_58,
  CLIENT_RECEIPT_PAPER_A5
];

const STORAGE_KEY = 'aman:clientReceiptPaperFormat';
/** Last 58/80 choice — kept when the main format switches to A5. */
const THERMAL_WIDTH_KEY = 'aman:clientReceiptThermalWidth';

const A5_ALIASES = new Set(['a5', 'a5portrait', 'a5_portrait']);
const THERMAL_58_ALIASES = new Set(['thermal58', '58', '58mm', 'thermal_58', 'bon58']);
const THERMAL_80_ALIASES = new Set([
  'thermal80',
  '80',
  '80mm',
  'thermal_80',
  'thermal',
  'bon80',
  'bon'
]);

const A5_PAGE_WIDTH_MM = 148;
const A5_PAGE_HEIGHT_MM = 210;
/** Ceiling only — Electron measures content height for actual pageSize. */
const THERMAL_PAGE_HEIGHT_CEILING_MM = 800;

export function normalizeClientReceiptPaperFormat(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return CLIENT_RECEIPT_PAPER_THERMAL_80;
  if (A5_ALIASES.has(raw)) return CLIENT_RECEIPT_PAPER_A5;
  if (THERMAL_58_ALIASES.has(raw)) return CLIENT_RECEIPT_PAPER_THERMAL_58;
  if (THERMAL_80_ALIASES.has(raw)) return CLIENT_RECEIPT_PAPER_THERMAL_80;
  return CLIENT_RECEIPT_PAPER_THERMAL_80;
}

export function isClientReceiptA5Format(format) {
  return normalizeClientReceiptPaperFormat(format) === CLIENT_RECEIPT_PAPER_A5;
}

export function isClientReceiptThermalFormat(format) {
  return !isClientReceiptA5Format(format);
}

/** Client format → thermal profile key (`80mm` | `58mm`). */
export function clientPaperFormatToThermalWidth(format) {
  return normalizeClientReceiptPaperFormat(format) === CLIENT_RECEIPT_PAPER_THERMAL_58
    ? '58mm'
    : DEFAULT_THERMAL_PAPER;
}

function safeKvGet(key) {
  try {
    return kvGet(key);
  } catch {
    return null;
  }
}

function safeKvSet(key, value) {
  try {
    kvSet(key, value);
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Thermal-only preference for additions/merchant (never A5).
 * Survives switching the main client format to A5.
 */
export function getPreferredThermalClientPaperFormat() {
  const rawThermal = safeKvGet(THERMAL_WIDTH_KEY);
  if (rawThermal != null && String(rawThermal).trim() !== '') {
    const thermal = normalizeClientReceiptPaperFormat(rawThermal);
    if (isClientReceiptThermalFormat(thermal)) return thermal;
  }
  const main = normalizeClientReceiptPaperFormat(safeKvGet(STORAGE_KEY));
  return isClientReceiptThermalFormat(main) ? main : CLIENT_RECEIPT_PAPER_THERMAL_80;
}

export function getClientReceiptPaperFormat() {
  return normalizeClientReceiptPaperFormat(safeKvGet(STORAGE_KEY));
}

export function setClientReceiptPaperFormat(format) {
  const normalized = normalizeClientReceiptPaperFormat(format);
  safeKvSet(STORAGE_KEY, normalized);
  if (isClientReceiptThermalFormat(normalized)) {
    safeKvSet(THERMAL_WIDTH_KEY, normalized);
  }
  return normalized;
}

/**
 * Silent-print options for a client paper format (POS / modal / printReceipt).
 */
export function resolveClientReceiptSilentPrintPlan(format, opts = {}) {
  const paperFormat = normalizeClientReceiptPaperFormat(
    format ?? getClientReceiptPaperFormat()
  );

  if (paperFormat === CLIENT_RECEIPT_PAPER_A5) {
    return {
      paperFormat,
      isA5: true,
      paperWidth: DEFAULT_THERMAL_PAPER,
      profile: null,
      kind: 'document',
      margins: { marginType: 'none' },
      pageWidthMm: A5_PAGE_WIDTH_MM,
      pageHeightMm: A5_PAGE_HEIGHT_MM,
      timeoutMs: 8000,
      retries: 1,
      imageWaitMs: 0
    };
  }

  const paperWidth = resolveThermalPaperWidthKey(
    clientPaperFormatToThermalWidth(paperFormat)
  );
  const profile = resolveThermalPaperProfile(paperWidth, opts.language || 'fr');

  return {
    paperFormat,
    isA5: false,
    paperWidth,
    profile,
    kind: 'thermal',
    margins: THERMAL_RECEIPT_PRINT_MARGINS,
    // Match driver form width (~72 on 80mm rolls), not marketing roll diameter.
    pageWidthMm: thermalMediaWidthMm(profile),
    pageHeightMm: THERMAL_PAGE_HEIGHT_CEILING_MM,
    timeoutMs: 5000,
    retries: 0,
    imageWaitMs: 0
  };
}
